(() => {
  'use strict';

  class SearchResults {
    constructor(config) {
      this.config = config;
      this.section = config.closest('[data-section="search"]');
      this.query = config.dataset.query || '';
      this.url = new URL(window.location.href);
      this.cache = new Map();
      this.selectedTags = Array.from(this.section.querySelectorAll('[data-search-animal-link][data-animal-tag]'))
        .filter(link => this.url.searchParams.getAll('filter.p.m.pim.animal').includes(link.dataset.animalValue))
        .map(link => 'animal:' + link.dataset.animalTag.toLowerCase());
      this.results = this.section.querySelector('#search-results-all');
      if (!this.results) {
        this.results = document.createElement('div');
        this.results.id = 'search-results-all';
        this.results.className = 'search-section__results';
        this.section.querySelector('.search-section__container').appendChild(this.results);
      }
      this.tabs = this.results.querySelector('.search-section__type-tabs');
      if (!this.tabs) {
        this.tabs = document.createElement('nav');
        this.tabs.className = 'search-section__type-tabs';
        this.tabs.setAttribute('aria-label', config.dataset.typesLabel);
        this.results.prepend(this.tabs);
      }
      this.summary = this.section.querySelector('[data-search-summary]');
      if (!this.summary) {
        this.summary = document.createElement('p');
        this.summary.className = 'search-section__results-info';
        this.summary.dataset.searchSummary = '';
        this.section.querySelector('.search-section__container').insertBefore(this.summary, this.results);
      }
      this.summary.setAttribute('aria-live', 'polite');
      this.summary.setAttribute('aria-atomic', 'true');
      this.load();
    }

    label(key) { return this.config.dataset[key] || ''; }

    format(key, values) {
      let text = this.label(key);
      Object.entries(values).forEach(([name, value]) => { text = text.split('__' + name + '__').join(String(value)); });
      return text;
    }

    sourceUrl(type, next) {
      const url = new URL(next || this.url.href, this.url.origin);
      if (url.origin !== this.url.origin || url.pathname !== this.url.pathname) throw new Error('Unexpected search source URL');
      url.searchParams.delete('sections');
      url.searchParams.set('q', this.query);
      url.searchParams.set('options[unavailable_products]', 'show');
      url.searchParams.set('section_id', type === 'glossary' ? 'search-glossary' : this.config.dataset.sourceSection);
      if (type === 'glossary') url.searchParams.delete('type');
      else url.searchParams.set('type', type);
      if (type !== 'product') {
        Array.from(url.searchParams.keys()).forEach(key => { if (key.startsWith('filter.')) url.searchParams.delete(key); });
        url.searchParams.delete('sort_by');
        if (!next) url.searchParams.delete('page');
      }
      return url;
    }

    async page(url) {
      const key = url.toString();
      if (!this.cache.has(key)) {
        const request = fetch(key).then(async response => {
          if (!response.ok) throw new Error('Search source response failed');
          const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
          if (!doc.querySelector('[data-search-results-controller], [data-search-glossary-source]')) throw new Error('Search source markup missing');
          const page = doc.querySelector('[data-search-source-page]');
          const template = page?.querySelector('[data-search-source-items]');
          const cards = template ? Array.from(template.content.querySelectorAll('[data-search-result-card]')) : [];
          return {
            cards, total: Number(page?.dataset.total) || 0,
            pageItems: Number(page?.dataset.pageItems) || 0,
            next: page?.dataset.next || '',
            hero: doc.querySelector('#search-recommendation'),
            pagination: doc.querySelector('[data-search-product-pagination]'),
          };
        }).catch(error => { this.cache.delete(key); throw error; });
        this.cache.set(key, request);
      }
      return this.cache.get(key);
    }

    deduplicate(cards) {
      const seen = new Set();
      return cards.filter(card => {
        const key = card.dataset.resultUrl;
        if (!key || seen.has(key)) return false;
        seen.add(key);
        return true;
      });
    }

    eligible(card) {
      if (!this.selectedTags.length || card.dataset.resultType !== 'article') return true;
      const tags = (card.dataset.animalTags || '').toLowerCase().split(',').map(tag => tag.trim()).filter(Boolean);
      const animals = tags.filter(tag => tag.startsWith('animal:'));
      return !animals.length || this.selectedTags.some(tag => animals.includes(tag));
    }

    async source(type) {
      let url = this.sourceUrl(type);
      const visited = new Set();
      let cards = [];
      let first;
      while (url) {
        if (visited.has(url.toString())) throw new Error('Search source pagination repeated');
        visited.add(url.toString());
        const page = await this.page(url);
        first ||= page;
        cards.push(...page.cards.filter(card => card.dataset.resultType === type && this.eligible(card)));
        if (type === 'product' || !page.next) break;
        url = this.sourceUrl(type, page.next);
      }
      cards = this.deduplicate(cards);
      if (type === 'glossary') cards.sort((a, b) => Number(b.dataset.exact) - Number(a.dataset.exact) || a.dataset.resultTitle.localeCompare(b.dataset.resultTitle));
      // A complete single product page can exclude hidden test records exactly.
      const total = type === 'product' && (first.next || first.total > first.pageItems) ? first.total : cards.length;
      return { type, cards, total, first };
    }

    group(type) {
      const id = 'search-' + (type === 'glossary' ? 'glossary' : type + 's');
      let group = this.results.querySelector('#' + id);
      if (!group) {
        group = document.createElement('div');
        group.id = id;
        group.className = 'search-section__group';
        group.innerHTML = '<h2 class="search-section__group-heading"></h2><p data-search-shown class="search-section__results-info"></p><div class="search-section__grid" role="list"></div>';
        this.results.appendChild(group);
      }
      return group;
    }

    tab(type, count, text) {
      const target = type === 'all' ? '#search-results-all' : '#search-' + (type === 'glossary' ? 'glossary' : type + 's');
      let tab = Array.from(this.tabs.querySelectorAll('a')).find(link => link.getAttribute('href') === target);
      if (!tab) {
        tab = document.createElement('a');
        tab.href = target;
        tab.className = 'search-section__type-tab';
        this.tabs.appendChild(tab);
      }
      tab.hidden = !count;
      tab.textContent = text + ' (' + count + ')';
      return tab;
    }

    renderGroup(source) {
      const { type, cards, total } = source;
      const group = this.group(type);
      group.hidden = !cards.length;
      const label = this.label(type === 'glossary' ? 'glossaryLabel' : type + 'sLabel');
      group.querySelector('h2').textContent = label + ' (' + total + ')';
      let shown = group.querySelector('[data-search-shown]');
      if (!shown) {
        shown = document.createElement('p');
        shown.dataset.searchShown = '';
        shown.className = 'search-section__results-info';
        group.querySelector('h2').after(shown);
      }
      const list = group.querySelector('[role="list"]');
      const limit = type === 'article' ? Number(this.config.dataset.articleLimit) || 6 : type === 'page' ? Number(this.config.dataset.pageLimit) || 4 : cards.length;
      let visible = Math.min(limit, cards.length);
      group.querySelector('[data-search-more]')?.remove();
      const render = () => {
        list.replaceChildren(...cards.slice(0, visible).map(card => card.cloneNode(true)));
        shown.textContent = this.format('shownText', { shown: visible, count: total });
        shown.hidden = visible === total;
      };
      render();
      if (visible < cards.length) {
        const more = document.createElement('button');
        more.type = 'button';
        more.dataset.searchMore = '';
        more.className = 'search-section__type-tab';
        more.textContent = this.label('moreLabel');
        more.addEventListener('click', () => {
          const previous = visible;
          visible = Math.min(visible + limit, cards.length);
          render();
          if (visible === cards.length) {
            list.children[previous]?.querySelector('a')?.focus();
            more.remove();
          }
        });
        group.appendChild(more);
      }
      this.tab(type, total, label);
      if (type === 'product') this.renderProducts(source);
    }

    renderProducts(source) {
      const oldHero = this.results.querySelector('#search-recommendation');
      if (source.first.hero) {
        const hero = source.first.hero.cloneNode(true);
        if (oldHero) oldHero.replaceWith(hero);
        else this.group('product').before(hero);
      } else oldHero?.remove();
      const oldPagination = this.results.querySelector('[data-search-product-pagination]');
      const pagination = source.first.pagination?.cloneNode(true);
      if (pagination) {
        pagination.querySelectorAll('a').forEach(link => {
          const url = new URL(link.getAttribute('href'), this.url.origin);
          url.searchParams.delete('section_id');
          url.searchParams.delete('sections');
          link.href = url.toString();
        });
        if (oldPagination) oldPagination.replaceWith(pagination);
        else this.group('product').appendChild(pagination);
      } else oldPagination?.remove();
      const heroTab = this.tabs.querySelector('a[href="#search-recommendation"]');
      if (heroTab) heroTab.hidden = !source.first.hero;
    }

    collectionUrls() {
      const root = this.section.querySelector('[data-search-collections-root]');
      if (!root || root.hidden) return [];
      return Array.from(root.querySelectorAll('[data-search-collections-list] [data-search-collection-item]'))
        .map(item => new URL(item.getAttribute('href'), this.url.origin).pathname);
    }

    async collectionSource() {
      const root = this.section.querySelector('[data-search-collections-root]');
      if (!root?.searchCollectionsPromise) return { type: 'collection', total: 0 };
      try {
        if (this.collectionsFailed) root.reloadSearchCollections();
        await root.searchCollectionsPromise;
        this.collectionsFailed = false;
        return { type: 'collection', total: new Set(this.collectionUrls()).size };
      } catch (error) {
        this.collectionsFailed = true;
        throw error;
      }
    }

    async load() {
      this.summary.setAttribute('aria-busy', 'true');
      this.section.querySelector('[data-search-source-error]')?.remove();
      const types = ['product', 'article', 'page', 'glossary', 'collection'];
      const responses = await Promise.allSettled(types.map(type => type === 'collection' ? this.collectionSource() : this.source(type)));
      const sources = responses.filter(result => result.status === 'fulfilled').map(result => result.value);
      sources.filter(source => source.type !== 'collection').forEach(source => this.renderGroup(source));
      const complete = responses.every(result => result.status === 'fulfilled');
      let count;
      if (complete) count = sources.reduce((sum, source) => sum + source.total, 0);
      else {
        const visibleUrls = Array.from(this.results.querySelectorAll('[data-search-result-card]')).map(card => card.dataset.resultUrl).concat(this.collectionUrls());
        const failedTypes = types.filter((type, index) => responses[index].status === 'rejected');
        const fallback = this.section.querySelector('[data-search-source-items]');
        if (fallback) visibleUrls.push(...Array.from(fallback.content.querySelectorAll('[data-search-result-card]')).filter(card => failedTypes.includes(card.dataset.resultType) && this.eligible(card)).map(card => card.dataset.resultUrl));
        count = new Set(visibleUrls).size;
      }
      this.summary.textContent = this.format(complete ? 'totalText' : 'pageText', { count, terms: this.query });
      if (this.config.dataset.titleSuffix !== undefined) document.title = this.summary.textContent + this.label('titleSuffix');
      this.summary.removeAttribute('aria-busy');
      this.tab('all', count, this.label('allLabel'));
      const empty = this.section.querySelector('[data-search-empty]');
      if (empty) empty.hidden = count > 0;
      const apply = this.section.querySelector('[data-search-filter-close].search-section__filter-flyout-apply');
      if (apply) apply.textContent = this.format('applyLabel', { count });
      if (!complete) {
        const error = document.createElement('p');
        error.dataset.searchSourceError = '';
        error.setAttribute('role', 'status');
        error.textContent = this.label('errorText') + ' ';
        const retry = document.createElement('button');
        retry.type = 'button';
        retry.className = 'search-section__type-tab';
        retry.textContent = this.label('retryLabel');
        retry.addEventListener('click', () => this.load());
        error.appendChild(retry);
        this.summary.after(error);
      }
      this.config.dataset.state = complete ? 'ready' : 'partial';
    }
  }

  document.querySelectorAll('[data-search-results-controller]').forEach(config => {
    if (config.dataset.query && !/tag:"animal:/.test(new URL(window.location.href).searchParams.get('q') || '')) new SearchResults(config);
  });
})();
